import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Link2, Unlink, RefreshCw, Upload, Download,
  Check, X, Clock, FileText, ChevronDown, ChevronUp, Eye, EyeOff,
  Building2, FileSpreadsheet, ArrowUpDown
} from 'lucide-react';
import api from '../services/api';

const PROVIDERS = {
  wfirma: {
    name: 'wFirma',
    color: 'from-blue-600 to-blue-800',
    accent: 'blue',
    description: 'Pełna integracja API: faktury, wydatki, kontrahenci, KSeF',
    fields: [
      { key: 'access_key', label: 'Access Key', placeholder: 'Klucz z Ustawienia > Aplikacje > Klucze API' },
      { key: 'secret_key', label: 'Secret Key', placeholder: 'Klucz jednorazowo wyświetlany przy tworzeniu' },
      { key: 'app_key', label: 'App Key', placeholder: 'Klucz aplikacji od wFirma' },
    ],
    extraFields: [
      { key: 'company_id', label: 'Company ID', placeholder: 'ID firmy (opcjonalnie)' }
    ],
    syncTypes: ['full', 'invoices', 'expenses', 'contractors'],
    canPush: true,
  },
  ifirma: {
    name: 'iFirma',
    color: 'from-emerald-600 to-emerald-800',
    accent: 'emerald',
    description: 'Integracja API: faktury, wydatki, KSeF',
    fields: [
      { key: 'user_name', label: 'Nazwa użytkownika (login)', placeholder: 'Email z konta iFirma' },
      { key: 'invoice_key', label: 'Klucz API Faktura', placeholder: 'Z Konfiguracja > API' },
    ],
    extraFields: [],
    syncTypes: ['full', 'invoices'],
    canPush: true,
  },
  symfonia: {
    name: 'Symfonia',
    color: 'from-purple-600 to-purple-800',
    accent: 'purple',
    description: 'Import/eksport plików JPK i XML',
    fields: [],
    extraFields: [],
    syncTypes: [],
    canPush: false,
    fileOnly: true,
  },
  optima: {
    name: 'Comarch Optima',
    color: 'from-red-600 to-red-800',
    accent: 'red',
    description: 'REST API (ERP XT): faktury, kontrahenci, produkty',
    fields: [
      { key: 'access_token', label: 'Access Token (OAuth 2.0)', placeholder: 'Token z panelu ERP XT > Publiczne API' },
    ],
    extraFields: [],
    syncTypes: ['full', 'invoices', 'contractors'],
    canPush: true,
  }
};

const SYNC_TYPE_LABELS = {
  full: 'Pełna synchronizacja',
  invoices: 'Tylko faktury',
  expenses: 'Tylko wydatki',
  contractors: 'Tylko kontrahenci',
};

export default function IntegrationPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncedData, setSyncedData] = useState([]);
  const [jpkImports, setJpkImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [showKeys, setShowKeys] = useState({});
  const [syncing, setSyncing] = useState({});
  const [testing, setTesting] = useState({});
  const [activeTab, setActiveTab] = useState('connections');
  const [formData, setFormData] = useState({});
  const [syncDataFilter, setSyncDataFilter] = useState({ provider: '', type: '' });

  const loadData = useCallback(async () => {
    try {
      const [settingsRes, historyRes, dataRes, jpkRes] = await Promise.all([
        api.get('/integrations/settings'),
        api.get('/integrations/sync-history'),
        api.get('/integrations/synced-data'),
        api.get('/integrations/jpk-imports'),
      ]);
      
      const settingsMap = {};
      (settingsRes.data.settings || []).forEach(s => { settingsMap[s.provider] = s; });
      setSettings(settingsMap);
      setSyncHistory(historyRes.data.history || []);
      setSyncedData(dataRes.data.data || []);
      setJpkImports(jpkRes.data.imports || []);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveSettings = async (provider) => {
    const config = PROVIDERS[provider];
    const keys = {};
    config.fields.forEach(f => { keys[f.key] = formData[`${provider}_${f.key}`] || ''; });
    
    try {
      await api.post('/integrations/settings', {
        provider,
        api_keys: keys,
        company_id: formData[`${provider}_company_id`] || null,
        enabled: true,
      });
      toast.success(`Ustawienia ${config.name} zapisane`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zapisu');
    }
  };

  const handleDeleteIntegration = async (provider) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć integrację ${PROVIDERS[provider].name}?`)) return;
    try {
      await api.delete(`/integrations/settings/${provider}`);
      toast.success('Integracja usunięta');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd');
    }
  };

  const handleTestConnection = async (provider) => {
    setTesting(prev => ({ ...prev, [provider]: true }));
    try {
      const res = await api.post(`/integrations/test-connection/${provider}`);
      if (res.data.status === 'ok') {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd połączenia');
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleSync = async (provider, syncType = 'full') => {
    setSyncing(prev => ({ ...prev, [provider]: true }));
    try {
      const res = await api.post(`/integrations/sync/${provider}`, { sync_type: syncType });
      toast.success(res.data.message);
      setTimeout(() => loadData(), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd synchronizacji');
    } finally {
      setSyncing(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleJpkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const fd = new FormData();
    fd.append('file', file);
    
    try {
      const res = await api.post('/integrations/import-jpk', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.message} (${res.data.records_count} rekordów)`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd importu JPK');
    }
    e.target.value = '';
  };

  const handleExportJpk = async (type) => {
    try {
      const res = await api.get(`/integrations/export-jpk/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `JPK_${type}_${new Date().toISOString().slice(0,10)}.xml`;
      a.click();
      toast.success('Plik JPK pobrany');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Brak danych do eksportu');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="integration-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Pulpit
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Integracje z oprogramowaniem księgowym</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">wFirma, iFirma, Comarch Optima, Symfonia — dwustronna synchronizacja danych</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 dark:border-slate-700 pb-3">
          {[
            { id: 'connections', label: 'Połączenia', icon: Link2 },
            { id: 'data', label: 'Zsynchronizowane dane', icon: FileText },
            { id: 'jpk', label: 'JPK / XML', icon: FileSpreadsheet },
            { id: 'history', label: 'Historia', icon: Clock },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={activeTab === tab.id ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : ''}
            >
              <tab.icon className="w-4 h-4 mr-1.5" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* CONNECTIONS TAB */}
        {activeTab === 'connections' && (
          <div className="space-y-4">
            {Object.entries(PROVIDERS).map(([key, config]) => {
              const isConnected = !!settings[key];
              const isExpanded = expandedProvider === key;
              
              return (
                <div
                  key={key}
                  data-testid={`provider-${key}`}
                  className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
                >
                  {/* Provider header */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-750"
                    onClick={() => setExpandedProvider(isExpanded ? null : key)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center`}>
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 dark:text-white">{config.name}</h3>
                          {isConnected ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              <Check className="w-3 h-3" /> Połączono
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                              <X className="w-3 h-3" /> Nie połączono
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{config.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isConnected && !config.fileOnly && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleTestConnection(key); }}
                            disabled={testing[key]}
                            data-testid={`test-${key}`}
                          >
                            {testing[key] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                            <span className="ml-1.5 text-xs">Test</span>
                          </Button>
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleSync(key); }}
                            disabled={syncing[key]}
                            data-testid={`sync-${key}`}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {syncing[key] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpDown className="w-3.5 h-3.5" />}
                            <span className="ml-1.5 text-xs">Synchronizuj</span>
                          </Button>
                        </>
                      )}
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>

                  {/* Expanded config form */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 dark:border-slate-700 p-5 bg-slate-50 dark:bg-slate-850">
                      {config.fileOnly ? (
                        <div className="text-center py-6">
                          <FileSpreadsheet className="w-12 h-12 mx-auto text-purple-500 mb-3" />
                          <p className="text-slate-600 dark:text-slate-300 mb-4">
                            Symfonia korzysta z importu/eksportu plików JPK i XML.<br/>
                            Przejdź do zakładki <strong>JPK / XML</strong> aby importować lub eksportować dane.
                          </p>
                          <Button onClick={() => setActiveTab('jpk')} className="bg-purple-600 hover:bg-purple-700 text-white">
                            <FileSpreadsheet className="w-4 h-4 mr-2" /> Przejdź do JPK / XML
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {config.fields.map(field => (
                              <div key={field.key}>
                                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{field.label}</Label>
                                <div className="relative mt-1">
                                  <Input
                                    type={showKeys[`${key}_${field.key}`] ? 'text' : 'password'}
                                    placeholder={field.placeholder}
                                    value={formData[`${key}_${field.key}`] ?? settings[key]?.api_keys?.[field.key] ?? ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, [`${key}_${field.key}`]: e.target.value }))}
                                    data-testid={`input-${key}-${field.key}`}
                                    className="pr-10 dark:bg-slate-800 dark:border-slate-600"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowKeys(prev => ({ ...prev, [`${key}_${field.key}`]: !prev[`${key}_${field.key}`] }))}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                  >
                                    {showKeys[`${key}_${field.key}`] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            ))}
                            {config.extraFields?.map(field => (
                              <div key={field.key}>
                                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">{field.label}</Label>
                                <Input
                                  type="text"
                                  placeholder={field.placeholder}
                                  value={formData[`${key}_${field.key}`] ?? settings[key]?.company_id ?? ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, [`${key}_${field.key}`]: e.target.value }))}
                                  data-testid={`input-${key}-${field.key}`}
                                  className="mt-1 dark:bg-slate-800 dark:border-slate-600"
                                />
                              </div>
                            ))}
                          </div>

                          {/* Sync type selection */}
                          {isConnected && config.syncTypes.length > 1 && (
                            <div>
                              <Label className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2 block">Synchronizuj wybrane:</Label>
                              <div className="flex flex-wrap gap-2">
                                {config.syncTypes.map(st => (
                                  <Button
                                    key={st}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSync(key, st)}
                                    disabled={syncing[key]}
                                    className="text-xs"
                                  >
                                    <ArrowUpDown className="w-3 h-3 mr-1" />
                                    {SYNC_TYPE_LABELS[st]}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex gap-2 pt-2">
                            <Button
                              onClick={() => handleSaveSettings(key)}
                              data-testid={`save-${key}`}
                              className="bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                            >
                              <Check className="w-4 h-4 mr-1.5" /> Zapisz ustawienia
                            </Button>
                            {isConnected && (
                              <Button
                                variant="outline"
                                onClick={() => handleDeleteIntegration(key)}
                                className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950"
                              >
                                <Unlink className="w-4 h-4 mr-1.5" /> Rozłącz
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* SYNCED DATA TAB */}
        {activeTab === 'data' && (
          <div>
            <div className="flex gap-3 mb-4">
              <select
                value={syncDataFilter.provider}
                onChange={(e) => setSyncDataFilter(prev => ({ ...prev, provider: e.target.value }))}
                className="px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
                data-testid="filter-provider"
              >
                <option value="">Wszystkie źródła</option>
                <option value="wfirma">wFirma</option>
                <option value="ifirma">iFirma</option>
                <option value="optima">Comarch Optima</option>
                <option value="symfonia">Symfonia</option>
              </select>
              <select
                value={syncDataFilter.type}
                onChange={(e) => setSyncDataFilter(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
                data-testid="filter-type"
              >
                <option value="">Wszystkie typy</option>
                <option value="invoice">Faktury</option>
                <option value="expense">Wydatki</option>
                <option value="contractor">Kontrahenci</option>
              </select>
            </div>

            {syncedData.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <FileText className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400">Brak zsynchronizowanych danych</p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Połącz się z oprogramowaniem księgowym i uruchom synchronizację</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-750">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Źródło</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Typ</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Dane</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Data sync</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {syncedData
                      .filter(d => (!syncDataFilter.provider || d.provider === syncDataFilter.provider) && 
                                   (!syncDataFilter.type || d.type === syncDataFilter.type))
                      .slice(0, 50)
                      .map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-750">
                          <td className="px-4 py-2.5">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              item.provider === 'wfirma' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                              item.provider === 'ifirma' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                              item.provider === 'optima' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                              'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            }`}>
                              {item.provider}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300 capitalize">{item.type}</td>
                          <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                            {item.data?.fullnumber || item.data?.name || item.data?.numer || JSON.stringify(item.data).slice(0, 80)}
                          </td>
                          <td className="px-4 py-2.5 text-slate-400 text-xs">{item.synced_at?.slice(0, 16).replace('T', ' ')}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* JPK / XML TAB */}
        {activeTab === 'jpk' && (
          <div className="space-y-6">
            {/* Import */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-500" /> Import pliku JPK / XML
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                Wgraj plik JPK_VAT, JPK_FA, JPK_KPiR lub dowolny XML z Symfonii lub innego programu księgowego.
              </p>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors">
                <Upload className="w-4 h-4" /> Wybierz plik XML
                <input type="file" accept=".xml" className="hidden" onChange={handleJpkUpload} data-testid="jpk-upload-input" />
              </label>
            </div>

            {/* Export */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-500" /> Eksport JPK
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                Eksportuj zsynchronizowane dane jako plik XML kompatybilny z Symfonią.
              </p>
              <div className="flex flex-wrap gap-2">
                {['JPK_VAT', 'JPK_FA', 'JPK_KPiR'].map(type => (
                  <Button key={type} variant="outline" onClick={() => handleExportJpk(type)} data-testid={`export-${type}`}>
                    <Download className="w-4 h-4 mr-1.5" /> {type}
                  </Button>
                ))}
              </div>
            </div>

            {/* Import history */}
            {jpkImports.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Historia importów</h3>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-750">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-medium text-slate-500 uppercase">Plik</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-slate-500 uppercase">Typ</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-slate-500 uppercase">Rekordy</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-slate-500 uppercase">Data</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {jpkImports.map((imp, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{imp.filename}</td>
                        <td className="px-4 py-2"><span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">{imp.type}</span></td>
                        <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{imp.records_count}</td>
                        <td className="px-4 py-2 text-slate-400 text-xs">{imp.imported_at?.slice(0, 16).replace('T', ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div>
            {syncHistory.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <Clock className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400">Brak historii synchronizacji</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-750">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Źródło</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Kierunek</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Typ</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Elementy</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase">Data</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {syncHistory.map((h, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-300">{h.provider}</td>
                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{h.direction === 'pull' ? 'Import' : 'Eksport'}</td>
                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{SYNC_TYPE_LABELS[h.sync_type] || h.sync_type}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            h.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            h.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            h.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {h.status === 'completed' ? 'Zakończono' : h.status === 'in_progress' ? 'W toku...' : h.status === 'failed' ? 'Błąd' : h.status}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{h.items_synced}</td>
                        <td className="px-4 py-2.5 text-slate-400 text-xs">{h.started_at?.slice(0, 16).replace('T', ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
